import { useCallback, useEffect, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  TERMINAL_INDEX_STATUSES,
  refreshFilesInCache,
} from '@/hooks/files/cache/fileCacheSync'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { fetchOrganizationFilesIndexStatus } from '@/hooks/organization/useOrganizationFilesIndexStatus'
import {
  pursuitFilesIndexStatusQueryKeys,
  pursuitFilesQueryKeys,
} from '@/hooks/pursuits/types'
import { usePursuitFiles } from '@/hooks/pursuits/usePursuitFiles'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { File, FileType } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { LiveBatchStatusResponse } from '@/types/Status'

const NOT_FOUND_GRACE_MS = 20_000
const LIVE_POLL_INTERVAL_MS = 5_000
const IDLE_POLL_INTERVAL_MS = 30 * 60 * 1000
const PUBLIC_FILE_TYPES = new Set<FileType>([
  'federal_contract_file',
  'federal_grant_file',
  'sbir_file',
  'sled_contract_file',
  'dibbs_contract_file',
])

interface PursuitFilesIndexStatusQueryKeyArgs<T> {
  pursuitId: UUID | ''
  select?: (data: LiveBatchStatusResponse) => T
}

type PublicFilesLiveStatusResponse = Omit<LiveBatchStatusResponse, 'job_type'>

export const boostPursuitFilesIndexPolling = ({
  queryClient,
  pursuitId,
  durationMs = NOT_FOUND_GRACE_MS,
}: {
  queryClient: QueryClient
  pursuitId: UUID | ''
  durationMs?: number
}) => {
  if (!pursuitId) {
    return
  }

  queryClient.setQueryData(
    pursuitFilesIndexStatusQueryKeys.pollBoost({ pursuitId }),
    Date.now() + durationMs,
  )
  void queryClient.refetchQueries({
    queryKey: pursuitFilesIndexStatusQueryKeys.live({ pursuitId }),
    type: 'active',
  })
}

const fetchPublicFilesIndexStatus = async (
  fileIds: UUID[],
  accessToken: string,
): Promise<PublicFilesLiveStatusResponse> => {
  if (fileIds.length === 0) {
    return {
      status: {},
      pending_count: 0,
      running_count: 0,
      current_step_name: null,
      is_running: false,
    }
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/jobs/live/index-public-files`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ file_ids: fileIds }),
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch public files index status: ${res.statusText}`,
      cause: res,
    })
  }
  return (await res.json()) as PublicFilesLiveStatusResponse
}

const buildPursuitLiveStatus = ({
  orgStatus,
  publicStatus,
  files,
}: {
  orgStatus: LiveBatchStatusResponse
  publicStatus: PublicFilesLiveStatusResponse
  files: Pick<File, 'id' | 'type'>[]
}): LiveBatchStatusResponse => {
  const status: LiveBatchStatusResponse['status'] = {}
  let hasOrgStatuses = false
  let hasPublicStatuses = false

  for (const file of files) {
    const sourceStatus = PUBLIC_FILE_TYPES.has(file.type)
      ? publicStatus.status[file.id]
      : orgStatus.status[file.id]
    if (!sourceStatus) {
      continue
    }
    status[file.id] = sourceStatus
    if (PUBLIC_FILE_TYPES.has(file.type)) {
      hasPublicStatuses = true
    } else {
      hasOrgStatuses = true
    }
  }

  const pending_count = Object.values(status).filter(
    (value) => value === 'pending',
  ).length
  const running_count = Object.values(status).filter(
    (value) => value === 'running',
  ).length

  return {
    status,
    job_type: 'index-pursuit-files',
    pending_count,
    running_count,
    current_step_name:
      (hasOrgStatuses ? orgStatus.current_step_name : null) ??
      (hasPublicStatuses ? publicStatus.current_step_name : null) ??
      null,
    is_running: pending_count > 0 || running_count > 0,
  }
}

const getRefetchInterval = ({
  data,
  graceStartedAt,
  hasActiveFileInCache,
  pollBoostUntil,
}: {
  data: LiveBatchStatusResponse | undefined
  graceStartedAt: number | null
  hasActiveFileInCache: boolean
  pollBoostUntil: number | null
}) => {
  if (pollBoostUntil !== null && Date.now() < pollBoostUntil) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (hasActiveFileInCache) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (!data) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (data.is_running) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (
    Object.keys(data.status).length === 0 &&
    graceStartedAt !== null &&
    Date.now() - graceStartedAt < NOT_FOUND_GRACE_MS
  ) {
    return LIVE_POLL_INTERVAL_MS
  }
  return IDLE_POLL_INTERVAL_MS
}

export const usePursuitFilesIndexStatus = <T = LiveBatchStatusResponse>({
  pursuitId,
  select,
}: PursuitFilesIndexStatusQueryKeyArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { activeOrgId } = useActiveOrgId()
  const queryClient = useQueryClient()
  const graceStartedAtRef = useRef<number | null>(null)
  const previousPursuitIdRef = useRef<UUID | ''>(pursuitId)
  const { data: pursuitFiles } = usePursuitFiles<Pick<File, 'id' | 'type'>[]>({
    pursuitId,
    select: (data) =>
      data.data.map((file) => ({ id: file.id, type: file.type })),
  })

  const selectFn = useCallback(
    (data: LiveBatchStatusResponse) =>
      select ? select(data) : (data as unknown as T),
    [select],
  )

  if (previousPursuitIdRef.current !== pursuitId) {
    previousPursuitIdRef.current = pursuitId
    graceStartedAtRef.current = null
  }

  const hasActiveFileInCache = useCallback(() => {
    if (!pursuitId) {
      return false
    }

    const files =
      queryClient.getQueryData<PaginatedResponse<Pick<File, 'index_status'>>>(
        pursuitFilesQueryKeys.pursuit({ pursuitId }),
      )?.data ?? []

    return files.some((file) => !TERMINAL_INDEX_STATUSES.has(file.index_status))
  }, [pursuitId, queryClient])

  return useQuery({
    queryFn: () => {
      if (pursuitId === '') {
        throw new Error('No pursuit ID provided.')
      }
      if (activeOrgId === '') {
        throw new Error('No organization ID provided.')
      }
      if (pursuitFiles === undefined) {
        throw new Error('Pursuit files are not loaded yet.')
      }

      const publicFileIds = pursuitFiles
        .filter((file) => PUBLIC_FILE_TYPES.has(file.type))
        .map((file) => file.id)

      return Promise.all([
        fetchOrganizationFilesIndexStatus(activeOrgId, accessToken),
        fetchPublicFilesIndexStatus(publicFileIds, accessToken),
      ]).then(([orgStatus, publicStatus]) =>
        buildPursuitLiveStatus({
          orgStatus,
          publicStatus,
          files: pursuitFiles,
        }),
      )
    },
    queryKey: pursuitFilesIndexStatusQueryKeys.live({ pursuitId }),
    refetchOnWindowFocus: true,
    enabled:
      pursuitId !== '' && activeOrgId !== '' && pursuitFiles !== undefined,
    staleTime: Infinity,
    refetchInterval: (query) => {
      const data = query.state.data
      const pollBoostUntil =
        queryClient.getQueryData<number>(
          pursuitFilesIndexStatusQueryKeys.pollBoost({ pursuitId }),
        ) ?? null
      const hasActiveFiles = hasActiveFileInCache()
      if (!data) {
        graceStartedAtRef.current = null
        return getRefetchInterval({
          data,
          graceStartedAt: graceStartedAtRef.current,
          hasActiveFileInCache: hasActiveFiles,
          pollBoostUntil,
        })
      }

      if (data.is_running || Object.keys(data.status).length > 0) {
        graceStartedAtRef.current = null
        queryClient.removeQueries({
          queryKey: pursuitFilesIndexStatusQueryKeys.pollBoost({ pursuitId }),
          exact: true,
        })
      } else if (graceStartedAtRef.current === null) {
        graceStartedAtRef.current = Date.now()
      }

      return getRefetchInterval({
        data,
        graceStartedAt: graceStartedAtRef.current,
        hasActiveFileInCache: hasActiveFiles,
        pollBoostUntil,
      })
    },
    select: selectFn,
  })
}

interface UseInvalidatePursuitFilesOnNotRunningArgs {
  pursuitId: UUID | ''
}

export const useInvalidatePursuitFilesOnNotRunning = ({
  pursuitId,
}: UseInvalidatePursuitFilesOnNotRunningArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()
  const prevActiveIdsRef = useRef<UUID[]>([])

  const { data } = usePursuitFilesIndexStatus({ pursuitId })

  useEffect(() => {
    const currentActiveIds = Object.keys(data?.status ?? {}) as UUID[]
    const previousActiveIds = prevActiveIdsRef.current
    const completedIds = previousActiveIds.filter(
      (fileId) => !currentActiveIds.includes(fileId),
    )
    prevActiveIdsRef.current = currentActiveIds

    if (completedIds.length === 0) {
      return
    }

    void refreshFilesInCache({
      queryClient,
      accessToken,
      fileIds: completedIds,
      pursuitId,
    })
  }, [accessToken, data?.status, pursuitId, queryClient])
}
